import React from 'react';
import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { radiusPx } from '../../../../theme/tokens';

function getScoreStyling(score = '') {
  const norm = String(score || '').toUpperCase();
  if (norm.includes('GREEN') || norm === 'HIGH' || norm === 'GOOD') {
    return {
      bg: '#e8f5e9',
      color: '#15803d',
      border: '#bbf7d0',
      icon: <CheckCircleIcon sx={{ fontSize: '15px !important', color: '#15803d' }} />,
    };
  }
  if (norm.includes('YELLOW') || norm === 'MEDIUM') {
    return {
      bg: '#fef3c7',
      color: '#b45309',
      border: '#fde68a',
      icon: <WarningAmberIcon sx={{ fontSize: '15px !important', color: '#b45309' }} />,
    };
  }
  if (norm.includes('RED') || norm === 'LOW') {
    return {
      bg: '#fee2e2',
      color: '#dc2626',
      border: '#fecaca',
      icon: <ErrorOutlineIcon sx={{ fontSize: '15px !important', color: '#dc2626' }} />,
    };
  }
  return {
    bg: '#f1f5f9',
    color: '#64748b',
    border: '#cbd5e1',
    icon: <HelpOutlineIcon sx={{ fontSize: '15px !important', color: '#64748b' }} />,
  };
}

export default function TemplateHealthCell({ row, health, onCheck }) {
  if (health?.loading) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <CircularProgress size={14} sx={{ color: '#059669' }} />
        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          Checking...
        </Typography>
      </Box>
    );
  }

  if (health?.error) {
    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              Health Check Failed
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#fca5a5' }}>
              {health.error}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Click to retry
            </Typography>
          </Box>
        }
        arrow
        placement="top"
      >
        <Chip
          size="small"
          label="Failed"
          onClick={(e) => {
            e.stopPropagation();
            onCheck?.(row);
          }}
          icon={<RefreshIcon sx={{ fontSize: '13px !important' }} />}
          sx={{
            bgcolor: '#fee2e2',
            color: '#dc2626',
            borderColor: '#fecaca',
            borderWidth: 1,
            borderStyle: 'solid',
            fontSize: '0.7rem',
            height: 22,
            cursor: 'pointer',
            fontWeight: 600,
            borderRadius: radiusPx.sm,
            '&:hover': {
              bgcolor: '#fecaca',
            },
          }}
        />
      </Tooltip>
    );
  }

  if (health && (health.qualityScore || health.status)) {
    const styling = getScoreStyling(health.qualityScore);
    const scoreLabel = String(health.qualityScore || 'UNKNOWN').toUpperCase();

    const tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#ffffff' }}>
          Health / Quality: {scoreLabel}
        </Typography>
        {health.status ? (
          <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1' }}>
            Status: {health.status}
          </Typography>
        ) : null}
        {health.rejectionReason ? (
          <Typography variant="caption" sx={{ display: 'block', color: '#fca5a5' }}>
            Reason: {health.rejectionReason}
          </Typography>
        ) : null}
        {health.lastChecked ? (
          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.5 }}>
            Checked: {new Date(health.lastChecked).toLocaleTimeString()}
          </Typography>
        ) : null}
        <Typography variant="caption" sx={{ display: 'block', color: '#38bdf8', mt: 0.5, fontStyle: 'italic' }}>
          Click to re-check
        </Typography>
      </Box>
    );

    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Chip
          size="small"
          icon={styling.icon}
          label={scoreLabel}
          onClick={(e) => {
            e.stopPropagation();
            onCheck?.(row);
          }}
          sx={{
            bgcolor: styling.bg,
            color: styling.color,
            borderColor: styling.border,
            borderWidth: 1,
            borderStyle: 'solid',
            fontSize: '0.7rem',
            height: 22,
            cursor: 'pointer',
            fontWeight: 700,
            borderRadius: radiusPx.sm,
            transition: 'all 0.15s ease',
            '&:hover': {
              opacity: 0.85,
              transform: 'scale(1.02)',
            },
          }}
        />
      </Tooltip>
    );
  }

  // Not checked yet: show subtle check button
  return (
    <Tooltip title="Click to check health" arrow placement="top">
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onCheck?.(row);
        }}
        sx={{
          color: '#64748b',
          p: 0.5,
          border: '1px dashed #cbd5e1',
          borderRadius: radiusPx.sm,
          transition: 'all 0.15s ease',
          '&:hover': {
            color: '#059669',
            borderColor: '#059669',
            bgcolor: '#ecfdf5',
          },
        }}
      >
        <MonitorHeartIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

